/**
 * Message thread view — scrollable message history with auto-scroll,
 * typing indicator, and message input.
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { listMessages, markChannelRead, type TeamMessage } from '@/api/team'
import { useTeamStore } from '@/store/team'
import { useAuthStore } from '@/store/auth'
import { renderMessageBody } from './MentionChip'
import { MessageInput } from './MessageInput'
import { Icon } from '@/components/ui/icon'
import { fmtRelative } from '@/lib/format'
import { cn } from '@/lib/utils'

interface Props {
  channelId: string
  channelName: string
  onBack: () => void
}

const EMPTY_MSGS: TeamMessage[] = []
const EMPTY_TYPING: string[] = []

export function MessageThread({ channelId, channelName, onBack }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const messages = useTeamStore((s) => s.messages[channelId] || EMPTY_MSGS)
  const prependMessages = useTeamStore((s) => s.prependMessages)
  const decrementUnread = useTeamStore((s) => s.decrementUnread)
  const typingUsers = useTeamStore((s) => s.typingUsers[channelId] || EMPTY_TYPING)

  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  // Fetch messages on mount — always refetch when channel opens
  // to pick up any messages missed while the panel was closed.
  const initialQ = useQuery({
    queryKey: ['team-messages', channelId],
    queryFn: async () => {
      const res = await listMessages(channelId, { limit: 50 })
      prependMessages(channelId, res.messages)
      if (res.messages.length < 50) setHasMore(false)
      return res.messages
    },
    staleTime: 5_000,
  })

  // Mark read on mount + when messages change
  useEffect(() => {
    markChannelRead(channelId).catch(() => {})
    decrementUnread(channelId)
  }, [channelId, messages.length, decrementUnread])

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  // Load older messages on scroll to top
  const loadOlder = useCallback(async () => {
    if (loadingMore || !hasMore || messages.length === 0) return
    setLoadingMore(true)
    try {
      const oldest = messages[0]
      const res = await listMessages(channelId, {
        before: oldest.created_at,
        limit: 50,
      })
      prependMessages(channelId, res.messages)
      if (res.messages.length < 50) setHasMore(false)
    } finally {
      setLoadingMore(false)
    }
  }, [channelId, messages, loadingMore, hasMore, prependMessages])

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (el && el.scrollTop < 40 && hasMore) {
      loadOlder()
    }
  }, [loadOlder, hasMore])

  const typingLabel = typingUsers
    .filter((u) => u !== currentUserId)
    .slice(0, 2)
    .join(', ')

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-hairline shrink-0">
        <button
          onClick={onBack}
          className="w-10 h-10 sm:w-6 sm:h-6 flex items-center justify-center text-ink-500 hover:text-ink-900 cursor-pointer"
        >
          <Icon name="arrow-left" variant="solid" className="text-xs" />
        </button>
        <span className="text-sm font-semibold text-ink-900 truncate">{channelName}</span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto px-3 py-2 space-y-2"
        style={{ scrollbarWidth: 'none' }}
      >
        {loadingMore && (
          <div className="text-center text-[10px] text-ink-400 py-1">Loading older...</div>
        )}
        {!initialQ.isLoading && messages.length === 0 && (
          <div className="text-center text-xs text-ink-400 py-8">
            No messages yet. Say hi!
          </div>
        )}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            isOwn={msg.sender_id === currentUserId}
          />
        ))}
        {typingLabel && (
          <div className="text-[10px] text-ink-400 italic pl-1">
            {typingLabel} typing...
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <MessageInput channelId={channelId} />
    </div>
  )
}

function MessageBubble({ msg, isOwn }: { msg: TeamMessage; isOwn: boolean }) {
  const initials = (msg.sender_name || msg.sender_email || '?')
    .split(/[\s@]/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('')

  return (
    <div className={cn('flex gap-2', isOwn && 'flex-row-reverse')}>
      {!isOwn && (
        <div className="w-6 h-6 rounded-full bg-ink-200 flex items-center justify-center text-[9px] font-semibold text-ink-600 shrink-0 mt-0.5">
          {initials}
        </div>
      )}
      <div className={cn('max-w-[80%] min-w-0', isOwn && 'text-right')}>
        {!isOwn && (
          <div className="text-[10px] text-ink-500 mb-0.5 truncate">
            {msg.sender_name || msg.sender_email}
          </div>
        )}
        <div
          className={cn(
            'inline-block px-2.5 py-1.5 rounded-lg text-xs leading-relaxed whitespace-pre-wrap break-words',
            isOwn
              ? 'bg-primary/15 text-ink-900'
              : 'bg-surface-alt text-ink-900',
          )}
        >
          {renderMessageBody(msg.body)}
        </div>
        <div className={cn('text-[9px] text-ink-400 mt-0.5', isOwn && 'text-right')}>
          {fmtRelative(msg.created_at)}
        </div>
      </div>
    </div>
  )
}
